import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ArtistRoute from './components/ArtistRoute';
import AuthCallback from './components/AuthCallback';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Landing from './pages/Landing';
import NotFound from './pages/NotFound';
import About from './pages/About';
import Admin from './pages/Admin';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Collection from './pages/Collection';
import BodyMapPage from './pages/BodyMapPage';
import Care from './pages/Care';
import Reminders from './pages/Reminders';
import Artists from './pages/Artists';
import Settings from './pages/Settings';
import Pricing from './pages/Pricing';
import PaymentSuccess from './pages/PaymentSuccess';
import SmartCheck from './pages/SmartCheck';
import TimeLapse from './pages/TimeLapse';
import ARPreview from './pages/ARPreview';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import DeliveryReturn from './pages/DeliveryReturn';
import DistanceSales from './pages/DistanceSales';
import ArtistDashboard from './pages/ArtistDashboard';
import ArtistLogin from './pages/ArtistLogin';

// Router wrapper to handle OAuth callback
function AppRouter() {
  const location = useLocation();

  // Check URL fragment (not query params) for session_id
  // This synchronous check prevents race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/artist/login" element={<ArtistLogin />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* User Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/collection" element={
        <ProtectedRoute>
          <Collection />
        </ProtectedRoute>
      } />
      <Route path="/body-map" element={
        <ProtectedRoute>
          <BodyMapPage />
        </ProtectedRoute>
      } />
      <Route path="/care" element={
        <ProtectedRoute>
          <Care />
        </ProtectedRoute>
      } />
      <Route path="/reminders" element={
        <ProtectedRoute>
          <Reminders />
        </ProtectedRoute>
      } />
      <Route path="/artists" element={
        <ProtectedRoute>
          <Artists />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccess />
        </ProtectedRoute>
      } />
      <Route path="/smart-check" element={
        <ProtectedRoute>
          <SmartCheck />
        </ProtectedRoute>
      } />
      <Route path="/timelapse/:tattooId" element={
        <ProtectedRoute>
          <TimeLapse />
        </ProtectedRoute>
      } />
      <Route path="/ar-preview" element={
        <ProtectedRoute>
          <ARPreview />
        </ProtectedRoute>
      } />

      {/* Artist Portal Routes */}
      <Route path="/artist/dashboard" element={
        <ArtistRoute>
          <ArtistDashboard />
        </ArtistRoute>
      } />

      {/* Admin Route - Protected */}
      <Route path="/admin" element={
        <AdminRoute>
          <Admin />
        </AdminRoute>
      } />

      {/* Public Pages */}
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/delivery-return" element={<DeliveryReturn />} />
      <Route path="/distance-sales" element={<DistanceSales />} />

      {/* 404 Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
